import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BgColorOptions, ColorOptions, HorizontalAlignmentOptions, HtmlTagOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsContent, NICmsContentField } from '@icms/core/content';
import { iconsOptions } from '../../styles/cms.config';
import { ButtonStyleComponent, TButtonStyles, buttonStylesOptions } from '../buttons/button-style/button-style.component';

export namespace NTextBox {
    export const ID = 'text';

    export interface IData {
        overlineText?: string;
        icon?: string;
        title?: string;
        titleStyle?: string;
        titleTag?: string;
        subtitle?: string;
        subtitleStyle?: string;
        buttons?: IButton[];
        textColor?: string;
        boxTextAlign?: string;
        boxBackgroundColor?: string;
    }

    export interface IButton {
        copy: string;
        link: string;
        style: TButtonStyles;
    }

    export const getFields = (backgroundBox = true, boxTextAlign = true, useButtons = true) => {
        const fields: NICmsContentField.IField<any, any>[] = [
            {
                key: 'overlineText',
                type: NICmsContentField.EFieldType.text,
                title: 'Overline Text',
                default: 'Lorem ipsum dolor sit amet'
            },
            <NICmsContentField.IChoiceField>{
                key: 'icon',
                type: NICmsContentField.EFieldType.choice,
                title: 'Icon',
                options: iconsOptions,
                previewMode: 'icon'
            },
            {
                key: 'title',
                type: NICmsContentField.EFieldType.textarea,
                title: 'Title',
                default: 'Long heading title on two lines lorem ipsum'
            },
            <NICmsContentField.IChoiceField>{
                key: 'titleStyle',
                type: NICmsContentField.EFieldType.choice,
                title: 'Title style',
                options: TextStyleOptions,
                default: 'h1'
            },
            <NICmsContentField.IChoiceField>{
                key: 'titleTag',
                type: NICmsContentField.EFieldType.choice,
                title: 'Title HTML tag',
                options: HtmlTagOptions,
                default: 'h3'
            },
            {
                key: 'subtitle',
                type: NICmsContentField.EFieldType.textarea,
                title: 'Subtitle',
                default: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.<br> Aenean pellentesque commodo pulvinar. '
            },
            <NICmsContentField.IChoiceField>{
                key: 'subtitleStyle',
                type: NICmsContentField.EFieldType.choice,
                title: 'Subtitle style',
                options: TextStyleOptions,
                default: 'p1'
            },
            <NICmsContentField.IChoiceField>{
                key: 'textColor',
                type: NICmsContentField.EFieldType.choice,
                previewMode: 'color',
                title: 'Text color',
                options: ColorOptions,
                default: 'col-1'
            }
        ];

        if (useButtons) {
            fields.push(<NICmsContentField.IRepeaterField>{
                key: 'buttons',
                type: NICmsContentField.EFieldType.repeater,
                title: 'Buttons',
                itemNameField: 'copy',
                itemName: 'Button',
                fields: [
                    {
                        key: 'copy',
                        type: NICmsContentField.EFieldType.text,
                        title: 'Copy',
                        default: 'Button label'
                    },
                    {
                        key: 'style',
                        type: NICmsContentField.EFieldType.choice,
                        title: 'Style',
                        options: buttonStylesOptions,
                        default: 'button'
                    },
                    {
                        key: 'link',
                        type: NICmsContentField.EFieldType.text,
                        title: 'Link',
                        default: '/'
                    }
                ]
            });
        }

        if (boxTextAlign) {
            fields.push(<NICmsContentField.IChoiceField>{
                key: 'boxTextAlign',
                type: NICmsContentField.EFieldType.choice,
                title: 'Text align',
                options: HorizontalAlignmentOptions,
                default: 'left'
            });
        }
        return backgroundBox
            ? [
                  ...fields,
                  <NICmsContentField.IChoiceField>{
                      key: 'boxBackgroundColor',
                      type: NICmsContentField.EFieldType.choice,
                      previewMode: 'color',
                      title: 'Box color',
                      options: BgColorOptions,
                      default: 'bg-col-w-90'
                  }
              ]
            : fields;
    };
}

@Component({
    selector: 'text-box',
    templateUrl: './text-box.html',
    imports: [ButtonStyleComponent, ICmsClientModule, HrefComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TextBoxComponent extends UiComponent {
    @ICmsContent({
        watchDepth: 1
    })
    @Input({
        required: true
    })
    data!: NTextBox.IData;

    // used if the boxTextAlign cms field is not present
    @Input()
    textAlign?: 'left' | 'center' | 'right' = 'center';

    get boxTextAlign() {
        return this.data.boxTextAlign || this.textAlign;
    }
}
